import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWireState } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { ROLE_ANNOTATOR_USER, ROLE_CHECK_MANAGER } from '@constants/roles'
import { sendConfirmationEmail } from '@email/actions'
import { toast } from 'react-toastify'
import { useWireValue } from '@forminator/react-wire'

const useAuthFormViewModel = () => {
    
    //region State
    
    const navigate = useNavigate()
    
    const [message, setMessage] = useState()
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [signInLoading, setSignInLoading] = useState(false)
    
    // New org name, or existing org invite code
    const [orgSignupValue, setOrgSignupValue] = useState('')
    const [newOrgExists, setNewOrgExists] = useState(false)
    const [orgJoinRequests, setOrgJoinRequests] = useState()
    
    const [afterSignInRedirectUrl, setAfterSignInRedirectUrl] = useWireState(store.afterSignInRedirectUrl)
    const remoteConfig = useWireValue(store.remoteConfig)
    const isAtrEnabled = useMemo(() => !!remoteConfig.atr?.enabled, [remoteConfig])
    
    //endregion State
    
    //region Methods
    
    const onSignUpSubmit = useCallback(async e => {
        
        e.preventDefault()
        
        if (!firstName?.length || !lastName?.length || !email?.length)
            return setMessage({
                text: 'Please fill in all the fields.',
                level: 'danger',
            })
        
        const user = { firstName, lastName, email }
        const userExists = await actions.checkUserExists(email.toLowerCase())
        
        if (userExists === true)
            return setMessage({
                text: 'User already exists in the system.',
                level: 'danger',
            })
        
        try {
            
            const linkedInTrackRes = window.lintrk('track', { conversion_id: 14262249 })
            
            console.log('Tracked LinkedIn conversion', {
                linkedInTrackRes,
                _linkedin_data_partner_ids: window._linkedin_data_partner_ids,
            })
            
        } catch (e) {
            
            console.error('Failed to track LinkedIn conversion', e)
            
        }
        
        try {
            actions.identifyHubspotUser()
        } catch (e) {
            console.error('Failed to identify HubSpot user', e)
        }
        
        try {
            
            await actions.signUp(user)
            await sendConfirmationEmail(user.email.toLowerCase(), user.email.toLowerCase())
            
            navigate(`/emailsent?email=${user.email.toLowerCase()}`)
            
        } catch (e) {
            
            console.error('signUpWithEmail', e)
            
            return setMessage({
                text: 'An error occurred during signup. Please try again.',
                level: 'danger',
            })
            
        }
        
    }, [firstName, lastName, email])
    
    const onSignInSubmit = useCallback(async (e, adminDebugSavedLogin = null) => {
        
        e?.preventDefault()
        
        // Support signing in with locally saved, encrypted credentials (admins only, in develop only)
        const signInFn = adminDebugSavedLogin
            ? actions.signInAdminDebugSavedLogin
            : actions.signIn
        
        const { isLoggedIn, user, errorStatus } = await signInFn(
            adminDebugSavedLogin?.email ?? email.toLowerCase(),
            adminDebugSavedLogin?.password ?? password,
        )
        
        if (isLoggedIn) {
            
            if (user.roles?.some(it => it.name === ROLE_ANNOTATOR_USER))
                return navigate('/partners')
            
            if (!user.organizations.length)
                return navigate('/setorganizations')
            
            if (user.roles?.some(it => it.name === ROLE_CHECK_MANAGER) && user.roles.length === 1)
                return navigate('/checks')
            
            if (isAtrEnabled)
                return navigate('/atr')
            
            let nextUrl = '/collections'
            
            // If they were redirected here from another part of the app, clear it & navigate there
            if (afterSignInRedirectUrl) {
                nextUrl = afterSignInRedirectUrl
                setAfterSignInRedirectUrl(null)
            }
            
            return navigate(nextUrl)
            
        }
        
        if (errorStatus === 401)
            setMessage({ text: 'Account pending administrator approval', level: 'danger' })
        else
            setMessage({ text: 'Incorrect email or password', level: 'danger' })
        
    }, [email, password, afterSignInRedirectUrl, isAtrEnabled])
    
    const checkOrgExists = useCallback(async orgName => {
        
        setNewOrgExists(false)
        
        try {
            
            const res = await actions.checkOrgExists(orgName)
            
            setNewOrgExists(res)
            
        } catch (e) {
            
            console.error('checkOrgExists', e)
            
        }
        
    }, [])
    
    const fetchOrgJoinRequests = useCallback(async () => {
        
        try {
            
            const res = await actions.getOrgJoinRequestsForUser()
            
            setOrgJoinRequests(res)
            
        } catch (e) {
            
            console.error('fetchOrgJoinRequests', e)
            
            setOrgJoinRequests([])
            
            // @todo handle error
            
        }
        
    }, [])
    
    const onJoinOrganizationSubmit = useCallback(async e => {
        
        e.preventDefault()
        
        try {
            
            await actions.requestToJoinOrg(orgSignupValue)
            await fetchOrgJoinRequests()
            
        } catch (e) {
            
            console.error('onJoinOrganizationSubmit', e)
            
            toast.error('Failed to request to join organization. Please try again.')
            
        }
        
    }, [orgSignupValue])
    
    const onCreateOrganizationSubmit = useCallback(async e => {
        
        e.preventDefault()
        
        try {
            
            await actions.createOrg(orgSignupValue)
            await actions.fetchProfile('organizations')
            
            navigate('/collections')
            
        } catch (e) {
            
            console.error('onCreateOrganizationSubmit', e)
            toast.error('Failed to create new organization')
            
        }
        
    }, [email, orgSignupValue])
    
    //endregion Methods
    
    return {
        
        message,
        setMessage,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        email,
        setEmail,
        password,
        setPassword,
        orgSignupValue,
        setOrgSignupValue,
        newOrgExists,
        orgJoinRequests,
        setOrgJoinRequests,
        signInLoading,
        setSignInLoading,
        
        onSignUpSubmit,
        onSignInSubmit,
        onJoinOrganizationSubmit,
        checkOrgExists,
        fetchOrgJoinRequests,
        onCreateOrganizationSubmit,
        signOut: actions.signOut,
        
    }
    
}

export default useAuthFormViewModel
